import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { verifyEmail, resendVerification } from '../../api/auth.api';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { getApiErrorMessage } from '../../utils/apiError';
import toast from 'react-hot-toast';
import styles from './VerifyEmailPage.module.css';

const RESEND_COOLDOWN = 60;

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const { user, refreshUser, isAuthenticated } = useAuth();

  const [code, setCode] = useState('');
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // If not authenticated, redirect to login
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // If already verified, redirect to dashboard
  useEffect(() => {
    if (user?.emailVerified) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setServerError('');

      if (!code) {
        setServerError('Введите код подтверждения');
        return;
      }
      if (!/^\d{6}$/.test(code)) {
        setServerError('Код состоит из 6 цифр');
        return;
      }

      setLoading(true);
      try {
        await verifyEmail(code);
        toast.success('Email подтверждён!');
        await refreshUser();
        navigate('/dashboard', { replace: true });
      } catch (err: unknown) {
        setServerError(getApiErrorMessage(err, 'Ошибка при подтверждении. Попробуйте позже.'));
      } finally {
        setLoading(false);
      }
    },
    [code, navigate, refreshUser],
  );

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || resending) return;

    setResending(true);
    try {
      await resendVerification();
      toast.success('Код отправлен повторно');
      setResendCooldown(RESEND_COOLDOWN);
      setServerError('');
    } catch (err: unknown) {
      setServerError(getApiErrorMessage(err, 'Не удалось отправить код. Попробуйте позже.'));
    } finally {
      setResending(false);
    }
  }, [resendCooldown, resending]);

  if (!isAuthenticated || user?.emailVerified) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <h1 className={styles.title}>Подтверждение email</h1>
          <p className={styles.subtitle}>
            Мы отправили 6-значный код на{' '}
            <span className={styles.emailHighlight}>{user?.email}</span>
          </p>

          {serverError && (
            <div className={styles.serverError}>{serverError}</div>
          )}

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <Input
              label="Код подтверждения"
              type="text"
              placeholder="123456"
              value={code}
              // Без maxLength: он обрезает вставку раньше, чем мы уберём пробелы
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoComplete="one-time-code"
              inputMode="numeric"
            />
            <Button type="submit" fullWidth loading={loading} size="lg">
              Подтвердить
            </Button>
          </form>

          <div className={styles.resendRow}>
            {resendCooldown > 0 ? (
              <span>Отправить повторно через {resendCooldown} сек.</span>
            ) : (
              <button
                type="button"
                className={styles.resendLink}
                onClick={handleResend}
                disabled={resending}
              >
                Отправить код повторно
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
